import { useStore } from 'vue-livestore';
import { tables, events } from '@/livestore/schema.js';
import type { TimeEntry } from '@/domain/time-entry/TimeEntry.js';
import {
  detectColumnMap,
  validateRow,
  overlaps,
  type ColMap,
  type ImportOptions,
  type ImportResult,
} from '@/domain/transfer/SheetImport.js';

export {
  detectColumnMap,
  validateRow,
  overlaps,
  parseDateTime,
  type ColMap,
  type ValidationResult,
} from '@/domain/transfer/SheetImport.js';

export function parseSheetId(url: string): string | null {
  const m = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/.exec(String(url));
  return m ? m[1] : null;
}

function applyRow(
  candidate: Partial<TimeEntry>,
  strategy: string,
  existing: TimeEntry[],
  now: number,
  store: any,
): 'imported' | 'skipped' {
  const conflict = existing.find(e => overlaps(candidate, e));
  if (strategy === 'skip' && conflict) return 'skipped';
  if (strategy === 'overwrite' && conflict) {
    store.commit(
      events.timeEntryUpdated({
        id: conflict.id,
        ...candidate,
        is_edited: 1,
        modified_timestamp: now,
        updated_at: now,
      } as any),
    );
    return 'imported';
  }
  store.commit(
    events.timeEntryCreated({
      id: crypto.randomUUID(),
      ...candidate,
      created_at: now,
      is_deleted: 0,
      is_edited: 0,
      modified_timestamp: now,
      original_timestamp: now,
      updated_at: now,
    }),
  );
  return 'imported';
}

function importSheetRow(
  rawRow: string[],
  colMap: ColMap,
  strategy: string,
  defaultWorkplaceId: string | null,
  existing: TimeEntry[],
  now: number,
  store: any,
): 'imported' | 'skipped' {
  const result = validateRow(rawRow, colMap);
  if (!result.valid) return 'skipped';
  const wpIdx = colMap.workplace_id;
  const wpVal = wpIdx === undefined ? null : rawRow[wpIdx];
  const candidate: Partial<TimeEntry> = {
    ...result.entry,
    workplace_id: wpVal ?? defaultWorkplaceId,
  };
  return applyRow(candidate, strategy, existing, now, store);
}

interface ProcessSheetRowOpts {
  colMap: ColMap;
  defaultWorkplaceId: string | null;
  errors: string[];
  existing: TimeEntry[];
  idx: number;
  now: number;
  rawRow: string[];
  store: any;
  strategy: string;
}

function processSheetRow({
  colMap,
  defaultWorkplaceId,
  errors,
  existing,
  idx,
  now,
  rawRow,
  store,
  strategy,
}: ProcessSheetRowOpts): { imported: number; skipped: number } {
  if (!rawRow.some(Boolean)) return { imported: 0, skipped: 0 };
  const result = validateRow(rawRow, colMap);
  if (!result.valid) {
    errors.push(`Row ${idx + 2}: ${(result as any).error}`);
    return { imported: 0, skipped: 0 };
  }
  const outcome = importSheetRow(
    rawRow,
    colMap,
    strategy,
    defaultWorkplaceId,
    existing,
    now,
    store,
  );
  return outcome === 'imported' ? { imported: 1, skipped: 0 } : { imported: 0, skipped: 1 };
}

export async function importSheetValues(
  values: string[][],
  options: ImportOptions = {},
): Promise<ImportResult> {
  const { store } = useStore();
  const { defaultWorkplaceId = null, strategy = 'skip' } = options;
  if (!values.length) return { errors: [], imported: 0, skipped: 0 };

  const [headerRow, ...dataRows] = values;
  const colMap = detectColumnMap(headerRow);
  if (colMap.clock_in === undefined || colMap.clock_out === undefined) {
    return { errors: ['Could not find clock_in and clock_out columns'], imported: 0, skipped: 0 };
  }

  const existing = store.query(tables.timeEntries.select().where({ is_deleted: 0 }));
  const now = Date.now();
  let imported = 0,
    skipped = 0;
  const errors: string[] = [];

  for (const [idx, rawRow] of dataRows.entries()) {
    const r = processSheetRow({
      colMap,
      defaultWorkplaceId,
      errors,
      existing,
      idx,
      now,
      rawRow,
      store,
      strategy,
    });
    imported += r.imported;
    skipped += r.skipped;
  }

  return { errors, imported, skipped };
}
